import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';
import CurrencyDropdown from '../CurrencyDropdown.jsx';
import { useCurrency } from '../../contexts/CurrencyContext';
import LocationAutocomplete from '../LocationAutocomplete';
import { useTranslation } from 'react-i18next';

const HeroBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('comprar');
  const [propertyType, setPropertyType] = useState('');
  // Mínimo padrão deve ser 1 para não enviar 50.000 por engano
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const { currencySymbol } = useCurrency();
  const [showPropertyTypes, setShowPropertyTypes] = useState(false);
  const [guests, setGuests] = useState({ total: 0 });
  const [propertyTypes, setPropertyTypes] = useState([]); // tipos de imóvel (tiposimovel)
  const [categories, setCategories] = useState([]); // categorias vindas do painel
  const [location, setLocation] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Carrega tipos de imóvel e categorias do painel
  useEffect(() => {
    const fetchPropertyTypesAndCategories = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token
          ? { headers: { Authorization: `Bearer ${token}`, accept: 'application/json' } }
          : { headers: { accept: 'application/json' } };

        const [typesResponse, categoriesResponse] = await Promise.all([
          fetch(`${import.meta.env.VITE_URL_API}/tiposimovel`, headers),
          fetch(`${import.meta.env.VITE_URL_API}/categorias`, headers),
        ]);

        const typesData = await typesResponse.json();
        const categoriesData = await categoriesResponse.json();

        setPropertyTypes(Array.isArray(typesData) ? typesData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (error) {
        console.error('Error fetching property types or categories:', error);
        // Fallback simples para não quebrar o dropdown
        setPropertyTypes([]);
        setCategories([]);
      }
    };

    fetchPropertyTypesAndCategories();
  }, []);

  const titles = {
    comprar: {
      title: t('hero.title'),
      subtitle: t('hero.subtitle')
    },
    arrendar: {
      title: t('hero.title'),
      subtitle: t('hero.subtitle_rent')
    },
    ferias: {
      title: t('hero.title_vacation'),
      subtitle: t('hero.subtitle_vacation')
    }
  };

  // Filtra categorias conforme o tipo de busca selecionado (mesma ideia do SearchResults)
  const filteredCategories = categories.filter((category) => {
    const tipoCode = searchType === 'comprar' ? 1 : (searchType === 'arrendar' ? 2 : 3);
    return Number(category.tipoCATEGORIA) === Number(tipoCode);
  });

  // Opções mostradas no dropdown "Todos os tipos" da home
  const propertyTypeOptions = (() => {
    const MODE_LABELS = new Set(['comprar', 'arrendar', 'férias', 'ferias', 'todos os tipos']);

    // Começa sempre pelas categorias do modo atual
    let list = filteredCategories.map(c => ({
      id: c.codigoCATEGORIA,
      label: c.nomeCATEGORIA,
      slug: c.slugCATEGORIA,
      kind: 'category'
    }));

    // Se o painel tiver poucas categorias para esse modo, pode complementar com tipos gerais
    if (list.length < 6 && Array.isArray(propertyTypes) && propertyTypes.length) {
      const extraTypes = propertyTypes.map(t => ({
        id: t.codigoTIPOIMOVEL,
        label: t.nomeTIPOIMOVEL,
        slug: t.slugTIPOIMOVEL,
        kind: 'type'
      }));
      list = [...list, ...extraTypes];
    }

    const seen = new Set();
    return list.filter(opt => {
      const key = String(opt.label || '').trim().toLowerCase();
      if (!key || MODE_LABELS.has(key)) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  const clampPrice = (value) => {
    const n = Number(value) || 0;
    return Math.min(100000000, Math.max(0, n));
  };

  const formatPriceInput = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    const num = clampPrice(digits);
    return num ? new Intl.NumberFormat('pt-BR').format(num) : '';
  };

  const toDigits = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    return String(clampPrice(digits));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (location.trim()) params.set('location', location.trim());
    if (propertyType.trim()) params.set('propertyType', propertyType.trim());

    const internalSearchType =
      searchType === 'comprar' ? 'buy' :
      searchType === 'arrendar' ? 'rent' :
      searchType === 'ferias' ? 'vacation' : searchType;
    params.set('searchType', internalSearchType);

    const minPriceDigits = toDigits(priceRange.min);
    const maxPriceDigits = toDigits(priceRange.max);
    if (minPriceDigits) params.set('minPrice', minPriceDigits);
    if (maxPriceDigits) params.set('maxPrice', maxPriceDigits);

    if (searchType === 'ferias') {
      params.set('guests', `${guests.total} hóspedes`);
      if (dateStart) params.set('dateStart', dateStart);
      if (dateEnd) params.set('dateEnd', dateEnd);
    }

    const queryParams = params.toString();
    navigate(`/search-results?${queryParams}`);
  };

  const handleGuestsChange = (e) => {
    const value = Number(e.target.value.replace(/[^0-9]/g, '')) || 0;
    setGuests({ total: value });
  };

  const handleDateStartChange = (e) => {
    setDateStart(e.target.value);
  };

  const handleDateEndChange = (e) => {
    setDateEnd(e.target.value);
  };

  return (
    <div className="hero-banner">
      <div className="video-background">
        <video autoPlay muted loop playsInline>
          <source src="/videobanner.mp4" type="video/mp4" />
        </video>
        <div className="overlay"></div>
      </div>

      <div className="hero-content">
        <div id="home-search" className="search-container2">
          <h1 style={{ color: "#000"}}>{titles[searchType].title}</h1>
          <p style={{ color: "#000" }}>{titles[searchType].subtitle}</p>

          <div className="search-tabs">
            <button 
              className={searchType === 'comprar' ? 'active' : ''} 
              onClick={() => setSearchType('comprar')}
            >
              {t('hero.tabs.buy')}
            </button>
            <button 
              className={searchType === 'arrendar' ? 'active' : ''} 
              onClick={() => setSearchType('arrendar')}
            >
              {t('hero.tabs.rent')}
            </button>
            <button 
              className={searchType === 'ferias' ? 'active' : ''} 
              onClick={() => setSearchType('ferias')}
            >
              {t('hero.tabs.vacation')}
            </button>
          </div>

          <form onSubmit={handleSearch} className="search-form">
            <div className="form-field">
              <LocationAutocomplete
                value={location}
                onChange={setLocation}
                placeholder={t('hero.search.location')}
                style={{ color: "#000", borderColor: "#ccc" }}
              />
            </div>
            
            <div className="form-field">
              <div 
                className="property-type-select"
                onClick={() => setShowPropertyTypes(!showPropertyTypes)}
              >
                <input
                  type="text"
                  placeholder={t('hero.search.propertyType')}
                  value={propertyTypeOptions.find(o => (o.slug === propertyType || o.label === propertyType))?.label || ''}
                  readOnly
                />
                <i className="fas fa-chevron-down"></i>
                {showPropertyTypes && (
                  <div className="dropdown-menu">
                    <div className="dropdown-header">{t('hero.search.propertyType')}</div>
                    <div
                      className="dropdown-item"
                      onClick={() => {
                        setPropertyType('');
                        setShowPropertyTypes(false);
                      }}
                    >
                      {t('hero.search.propertyType')}
                    </div>
                    {propertyTypeOptions.map((opt) => (
                      <div
                        key={`${opt.kind}-${opt.id}`}
                        className="dropdown-item"
                        onClick={() => {
                          setPropertyType(opt.slug || opt.label);
                          setShowPropertyTypes(false);
                        }}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {searchType === 'ferias' ? (
              <>
                <div className="form-field">
                  <input
                    type="number"
                    min="0"
                    placeholder={t('hero.search.guests')}
                    value={guests.total || ''}
                    onChange={handleGuestsChange}
                  />
                </div>
                <div className="form-field dates-field">
                  <input
                    type="date"
                    placeholder={t('hero.search.checkIn')}
                    className="date-input"
                    value={dateStart}
                    onChange={handleDateStartChange}
                  />
                  <input
                    type="date"
                    placeholder={t('hero.search.checkOut')}
                    className="date-input"
                    value={dateEnd}
                    onChange={handleDateEndChange}
                  />
                </div>
              </>
            ) : (
              <div className="form-field price-range-wrapper">
                <label className="price-label">{t('hero.search.price')}</label>
                <div className="price-inputs">
                  <div className="price-input-box">
                    <span className="label-inside">{t('common.to')}</span>
                    <input 
                      type="text"
                      inputMode="numeric"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange({ ...priceRange, min: formatPriceInput(e.target.value) })}
                      placeholder=""
                    />
                    <span className="symbol-inside">{currencySymbol}</span>
                  </div>
                  <div className="price-input-box">
                    <span className="label-inside">{t('hero.search.to')}</span>
                    <input 
                      type="text"
                      inputMode="numeric"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange({ ...priceRange, max: formatPriceInput(e.target.value) })}
                      placeholder=""
                    />
                    <span className="symbol-inside">{currencySymbol}</span>
                  </div>
                </div>
              </div>
            )}

            <button type="submit" className="search-button">
              <i className="fas fa-search"></i>
              <span>{t('hero.search.button')}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;